import { useLocalStorageValue } from '@widgets/shared/compositions/useLocalStorageValue'
import { computed } from '@/composition/vue/compositionApi'
import type { DeepPartial } from '@/types'

type LocalStorageContainerValue = Record<string, string | number | object>
type LocalStorageKeyPath = {
  uniqueCode: string
  domain: string
  dataKey: string
}

export const useWidgetLocalStorageValue = <T extends string | number | object>(
  { uniqueCode, domain, dataKey }: LocalStorageKeyPath,
  defaultValue: T,
) => {
  const localStorageKey = `${domain}-${uniqueCode}`

  const localStorageContainer = useLocalStorageValue<LocalStorageContainerValue>(localStorageKey, {})

  const get = () => {
    return (localStorageContainer.get()[dataKey] as T) ?? defaultValue
  }

  const set = (value: T) => {
    localStorageContainer.set({
      ...localStorageContainer.get(),
      [dataKey]: value,
    })
  }

  const patch = (value: DeepPartial<T>) => {
    localStorageContainer.patch({
      [dataKey]: value,
    })
  }

  const remove = () => {
    const newValue = localStorageContainer.get()
    delete newValue[dataKey]
    localStorageContainer.set(newValue)
  }

  return {
    value: computed(() => (localStorageContainer.value.value[dataKey] as T) ?? defaultValue),
    get,
    set,
    patch,
    remove,
  }
}
